import { useIsFocused } from '@react-navigation/native';
import { useCallback, useRef, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  StyleSheet,
  Text,
  type ViewToken,
  View,
} from 'react-native';

import { ErrorView } from '@/src/components/ErrorView';
import { Screen } from '@/src/components/Screen';
import { useAuth } from '@/src/features/auth/use-auth';
import { CommentComposeModal } from '@/src/features/comments/CommentComposeModal';
import { FeedModeToggle } from '@/src/features/posts/FeedModeToggle';
import { PostCard } from '@/src/features/posts/PostCard';
import { PostPhotoViewerModal } from '@/src/features/posts/PostPhotoViewerModal';
import { PostVideoViewerModal } from '@/src/features/posts/PostVideoViewerModal';
import type { PublicPost } from '@/src/features/posts/post-service';
import { useFeedEvents } from '@/src/features/posts/use-feed-events';
import { useFollowingPosts } from '@/src/features/posts/use-following-posts';
import { useAppStore } from '@/src/state/app-store';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';
import { useRefreshableListInteractions } from '@/src/utils/refreshable-list-interactions';
import { useRegisterTabScrollHandler } from '@/src/utils/tab-scroll';

function EmptyFrienzzone({ followingCount }: { followingCount: number }) {
  return (
    <View style={styles.emptyState}>
      <Text style={styles.emptyTitle}>
        {followingCount === 0 ? 'Follow people first' : 'No posts here yet'}
      </Text>
      <Text style={styles.emptyText}>
        {followingCount === 0
          ? 'Tap names or profile photos in Home to open a profile, then follow people to build your Frienzzone.'
          : "Posts from people you're following will appear here when they share something."}
      </Text>
    </View>
  );
}

function LoadingFrienzzone() {
  return (
    <View style={styles.loadingState}>
      <ActivityIndicator color={colors.primary} />
      <Text style={styles.loadingText}>Loading Frienzzone...</Text>
    </View>
  );
}

export default function FriendsScreen() {
  const { user } = useAuth();
  const isFocused = useIsFocused();
  const feedMode = useAppStore((state) => state.feedModePreferences.frienzzone);
  const setFeedModePreference = useAppStore(
    (state) => state.setFeedModePreference,
  );
  const feed = useFollowingPosts(user?.uid, undefined, feedMode, isFocused);
  const listRef = useRef<FlatList<PublicPost>>(null);
  const [commentPost, setCommentPost] = useState<PublicPost | null>(null);
  const [videoViewerPostId, setVideoViewerPostId] = useState<string | null>(
    null,
  );
  const [videoViewerInitialPost, setVideoViewerInitialPost] =
    useState<PublicPost | null>(null);
  const [photoViewerPostId, setPhotoViewerPostId] = useState<string | null>(
    null,
  );
  const [photoViewerInitialPost, setPhotoViewerInitialPost] =
    useState<PublicPost | null>(null);
  const [photoViewerInitialIndex, setPhotoViewerInitialIndex] = useState(0);
  const feedEvents = useFeedEvents(
    'frienzzone',
    isFocused ? user?.uid : undefined,
  );
  const listInteractions = useRefreshableListInteractions({
    listRef,
    onRefresh: feed.refresh,
    refreshing: feed.refreshing,
  });

  useRegisterTabScrollHandler('friends', listInteractions.scrollToTop);

  const handleViewableItemsChanged = useRef(
    ({ viewableItems }: { viewableItems: ViewToken<PublicPost>[] }) => {
      viewableItems.forEach((viewableItem) => {
        if (viewableItem.isViewable && viewableItem.item?.id) {
          feedEvents.recordImpression(viewableItem.item.id);
        }
      });
    },
  ).current;
  const handleCommentPress = useCallback(
    (post: PublicPost) => {
      feedEvents.recordEvent({ postId: post.id, type: 'comment_open' }, true);
      setCommentPost(post);
    },
    [feedEvents],
  );
  const handleFeedFeedback = useCallback(
    (post: PublicPost, type: 'hide' | 'less_like_this') => {
      feedEvents.recordEvent({ postId: post.id, type }, true);
      feed.refresh();
    },
    [feed, feedEvents],
  );

  if (feed.error && feed.posts.length === 0) {
    return (
      <Screen>
        <ErrorView message={feed.error.message} />
      </Screen>
    );
  }

  return (
    <>
      <Screen scroll={false} contentStyle={styles.feedScreenContent}>
        <FlatList
          ref={listRef}
          contentContainerStyle={styles.feedContent}
          data={feed.posts}
          keyExtractor={(post) => post.id}
          ListFooterComponent={
            feed.loadingMore ? (
              <View style={styles.feedFooter}>
                <ActivityIndicator color={colors.primary} />
              </View>
            ) : null
          }
          ListEmptyComponent={
            feed.loading ? (
              <LoadingFrienzzone />
            ) : (
              <EmptyFrienzzone followingCount={feed.followingCount} />
            )
          }
          ListHeaderComponent={
            <View style={styles.feedHeader}>
              <FeedModeToggle
                mode={feedMode}
                onChange={(nextMode) => {
                  setFeedModePreference('frienzzone', nextMode);
                }}
              />
            </View>
          }
          onEndReached={feed.loadMore}
          onEndReachedThreshold={0.4}
          onRefresh={feed.refresh}
          onScroll={listInteractions.onScroll}
          onViewableItemsChanged={handleViewableItemsChanged}
          renderItem={({ item }) => (
            <PostCard
              post={item}
              onCommentPress={handleCommentPress}
              onDetailOpen={(post) => {
                feedEvents.recordEvent(
                  { postId: post.id, type: 'detail_open' },
                  true,
                );
              }}
              onFeedFeedback={
                feedMode === 'ranked' ? handleFeedFeedback : undefined
              }
              onPostEngagement={(post) => {
                feedEvents.recordEvent(
                  { postId: post.id, type: 'post_engagement' },
                  true,
                );
              }}
              onPhotoPress={(post, photoIndex) => {
                feedEvents.recordEvent(
                  { postId: post.id, type: 'detail_open' },
                  true,
                );
                setPhotoViewerInitialPost(post);
                setPhotoViewerPostId(post.id);
                setPhotoViewerInitialIndex(photoIndex);
              }}
              onVideoPress={(post) => {
                feedEvents.recordEvent(
                  { postId: post.id, type: 'detail_open' },
                  true,
                );
                setVideoViewerInitialPost(post);
                setVideoViewerPostId(post.id);
              }}
            />
          )}
          refreshing={feed.refreshing}
          scrollEventThrottle={16}
          showsVerticalScrollIndicator={false}
          viewabilityConfig={{ itemVisiblePercentThreshold: 55 }}
          {...listInteractions.webPullToRefreshHandlers}
        />
      </Screen>
      <CommentComposeModal
        post={commentPost}
        visible={Boolean(commentPost)}
        onClose={() => setCommentPost(null)}
      />
      <PostVideoViewerModal
        key={videoViewerPostId ?? 'post-video-viewer'}
        hasMorePosts={feed.hasMore}
        initialPost={videoViewerInitialPost}
        initialPostId={videoViewerPostId}
        loadingMorePosts={feed.loadingMore}
        posts={feed.posts}
        visible={Boolean(videoViewerPostId)}
        onClose={() => {
          setVideoViewerPostId(null);
          setVideoViewerInitialPost(null);
        }}
        onCommentPress={handleCommentPress}
        onLoadMorePosts={feed.loadMore}
        onPostEngagement={(post) => {
          feedEvents.recordEvent(
            { postId: post.id, type: 'post_engagement' },
            true,
          );
        }}
      />
      <PostPhotoViewerModal
        key={
          photoViewerPostId
            ? `${photoViewerPostId}:${photoViewerInitialIndex}`
            : 'post-photo-viewer'
        }
        hasMorePosts={feed.hasMore}
        initialPhotoIndex={photoViewerInitialIndex}
        initialPost={photoViewerInitialPost}
        initialPostId={photoViewerPostId}
        loadingMorePosts={feed.loadingMore}
        posts={feed.posts}
        visible={Boolean(photoViewerPostId)}
        onClose={() => {
          setPhotoViewerPostId(null);
          setPhotoViewerInitialPost(null);
          setPhotoViewerInitialIndex(0);
        }}
        onCommentPress={handleCommentPress}
        onLoadMorePosts={feed.loadMore}
        onPostEngagement={(post) => {
          feedEvents.recordEvent(
            { postId: post.id, type: 'post_engagement' },
            true,
          );
        }}
      />
    </>
  );
}

const styles = StyleSheet.create({
  feedScreenContent: {
    alignSelf: 'stretch',
    maxWidth: '100%',
    paddingBottom: 0,
    paddingHorizontal: 0,
    paddingTop: 0,
    width: '100%',
  },
  feedContent: {
    alignSelf: 'center',
    gap: spacing.lg,
    maxWidth: 640,
    paddingBottom: 0,
    paddingHorizontal: spacing.sm,
    paddingTop: spacing.md,
    width: '100%',
  },
  feedFooter: {
    alignItems: 'center',
    paddingVertical: spacing.md,
  },
  feedHeader: {
    gap: spacing.md,
  },
  emptyState: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.sm,
    padding: spacing.xl,
  },
  loadingState: {
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.sm,
    padding: spacing.xl,
  },
  loadingText: {
    color: colors.mutedText,
    fontSize: 15,
    fontWeight: '800',
  },
  emptyTitle: {
    color: colors.text,
    fontSize: 20,
    fontWeight: '900',
  },
  emptyText: {
    color: colors.mutedText,
    fontSize: 15,
    lineHeight: 22,
  },
});
